import DatePicker from '@allesauto/core/datePicker';
import InputField from '@allesauto/core/inputField';
import SearchDropdown, { type Option } from '@allesauto/core/searchDropdown';
import Textarea from '@allesauto/core/textarea';
import TextLink from '@allesauto/core/textLink';
import { useWhiteLabel } from '@allesauto/core/whiteLabel';
import {
  CashOption,
  PaymentType,
  SchemaTypes,
  TimeSlot,
} from '@allesauto/graphql';
import translate from '@allesauto/i18n';
import { pushAnalyticsEvent } from '@allesauto/utils/analytics';
import { SupportedLocales } from '@allesauto/utils/consts';
import {
  formatDateFormsRequest,
  getDealerAppointmentMinDate,
} from '@allesauto/utils/funcs';
import { Add32 } from '@carbon/icons-react';
import clsx from 'clsx';
import { useFormik } from 'formik';
import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import PrivacyNotice from '@/components/forms/PrivacyNotice/PrivacyNotice';
import { CustomerDetails } from '@/components/PDP/aboveFold/carDealerDetails/CarDealerDetails';
import ReCaptchaValidator, {
  ITokenValidator,
} from '@/components/ReCaptcha/ReCaptchaValidator';

import { useCarConfiguratorQueryParams } from '@/features/PDP/hooks';
import {
  sharedOverlayDetailsSteps,
  sharedOverlaySuccessTranslations,
} from '@/global/funcs/overlaySuccessSharedTranslations';
import useNewsletterSubscribeCheckbox from '@/global/hooks/useNewsletterSubscribeCheckbox';
import { useTestDriveBookingMutations } from '@/global/hooks/useTestDriveBookingMutations';
import {
  closeOverlay,
  openOverlay,
  setOverlayLoadingState,
} from '@/store/redux/actions/app';

import useTranslationTestDriveBooking from './testDriveBookingTranslation';
import OverlaySuccess from '../overlaySuccess/OverlaySuccess';
import {
  allowedPhonePattern,
  phoneInputChange,
  phoneOnPasteSanitizer,
} from '../validation/formUtils';
import Footer from '../../OverlayFormFooter/OverlayFormFooter';

export interface TestDriveBookingFormProps {
  vehicleSku: string;
  dealerId: string;
  carModel: string;
  carManufacturer: string;
  darkBg?: boolean;
  dealerName: string;
  contactPerson: SchemaTypes.ContactPerson;
  onBackToSearchClick: () => void;
  customerDetails?: CustomerDetails;
  paymentType: SchemaTypes.PaymentType | null;
  financeCalculationResult?: SchemaTypes.FinanceCalculationResultInput;
}

const TestDriveBookingForm: FC<
  React.PropsWithChildren<TestDriveBookingFormProps>
> = ({
  carModel,
  carManufacturer,
  dealerName,
  darkBg = false,
  onBackToSearchClick,
  vehicleSku,
  dealerId,
  contactPerson,
  customerDetails,
  paymentType,
  financeCalculationResult,
}) => {
  const { email, firstName, lastName, phone } = customerDetails ?? {};
  const whitelabel = useWhiteLabel();
  const intl = useIntl();
  const { requestTestDrive } = useTestDriveBookingMutations();
  const dispatch = useDispatch();
  const minimumDate = getDealerAppointmentMinDate(new Date(), 1);
  //AAA-5042 leave text area visible until extra phone field is added
  const [openedAddMessage, setOpenedAddMessage] = useState<boolean>(true);
  const [openedAdditionalFields, setOpenedAdditionalFields] =
    useState<boolean>(false);
  const {
    memoizedTestDriveBookingFormSchema,
    memoizedTestDriveBookingTranslations,
  } = useTranslationTestDriveBooking();

  const { carConfiguratorId } = useCarConfiguratorQueryParams();

  const overlayLoadingState = useSelector(
    (state: any) => state.app.overlayProps.isLoading
  );

  const className = {
    form: 'mx-[3px] mb-100px',
    container: 'bg-white h-full mb-20px',
    title: 'font-headline font-weight-bold leading-6 text-[20px] text-grey100',
    addLink:
      'fill-purple80 monochrome:fill-black h-[17.5px] inline-block outline-none w-[17.5px]',
    appointmentSubtitle: clsx(
      'font-weight-bold mt-60px text-p2',
      darkBg ? 'text-white' : 'text-grey90'
    ),
    dateTimeContainer: 'grid grid-cols-1 md:flex lg:flex xl:flex',
    datePicker: 'md:flex-1 lg:flex-1 xl:flex-1',
    searchDropdown:
      'mt-30px md:flex-1 md:ml-40px md:mt-0 lg:flex-1 lg:ml-40px lg:mt-0 xl:flex-1 xl:ml-40px xl:mt-0 z-[60]',
    newsletterPrivacy: 'mt-60px',
    privacyNotice: clsx(
      'font-regular mt-30px text-p3',
      darkBg ? 'text-white' : 'text-grey60'
    ),
    requiredFieldNote: clsx(
      'font-regular mt-30px pb-10px text-p3',
      darkBg ? 'text-white' : 'text-grey80'
    ),
    error: 'text-red60 text-p3 mt-10px',
  };

  const handleAddMessageClick = (ev: MouseEvent): void => {
    ev.preventDefault();
    setOpenedAddMessage(true);
  };

  const handleAdditionalFieldsClick = (ev: MouseEvent): void => {
    ev.preventDefault();
    setOpenedAdditionalFields(true);
  };

  const tokenValidator = useRef<ITokenValidator | undefined>();

  const setTokenValidator = useCallback((v: ITokenValidator) => {
    tokenValidator.current = v;
  }, []);

  //TODO: TO BE DEFINED WHEN WE HAVE FAVORITE LIST
  // const onFavListClick = () => {
  // };

  const memoizedOverlaySuccessTranslations = useMemo(() => {
    return sharedOverlaySuccessTranslations(intl, dealerName);
  }, [intl, dealerName]);

  const successOverlayDetailsSteps: React.ReactNode[] = useMemo(() => {
    return sharedOverlayDetailsSteps(intl, dealerName, contactPerson);
  }, [intl, dealerName, contactPerson]);

  const timeSlotOptions = [
    {
      value: TimeSlot.Morning,
      label: translate(
        intl,
        'ui.FORMS.TEST_DRIVE_BOOKING.TIMEWINDOW_LABELS.MORNING'
      ),
    },
    {
      value: TimeSlot.Noon,
      label: translate(
        intl,
        'ui.FORMS.TEST_DRIVE_BOOKING.TIMEWINDOW_LABELS.NOON'
      ),
    },
    {
      value: TimeSlot.Afternoon,
      label: translate(
        intl,
        'ui.FORMS.TEST_DRIVE_BOOKING.TIMEWINDOW_LABELS.AFTERNOON'
      ),
    },
  ];

  const {
    NewsletterSubscribeCheckbox,
    checkboxProps,
    subscribe: subscribeToNewsletter,
  } = useNewsletterSubscribeCheckbox({ isDarkBg: darkBg });

  useEffect(
    () => {
      pushAnalyticsEvent({
        event: 'dealerRequest',
        inquiry_topic: 'trade-in', //e.g. dealer
        inquiry_details: '', //e.g.success
        dealer_name: dealerName.toLocaleLowerCase(), //e.g. berthold kerres
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const formik = useFormik({
    initialValues: {
      userMsg: translate(
        intl,
        'ui.FORMS.TEST_DRIVE_BOOKING.USER_MSG_PREFILLED',
        {
          car: `${carManufacturer} ${carModel}`,
        }
      ),
      firstName: firstName ?? '',
      lastName: lastName ?? '',
      email: email ?? '',
      phone: phone ?? '',
      requestTimeSlot: { requestDate: '', requestTime: null },
      requestTimeSlotOptional: { requestDate: '', requestTime: null },
    },

    onSubmit: async (data) => {
      let token = '';

      if (process.env.NEXT_PUBLIC_FEATUREFLAG_RECAPTCHA) {
        token = (await tokenValidator.current?.validate()) || '';
        if (!token) {
          return false;
        }
      }

      dispatch(setOverlayLoadingState(true));

      const formData: SchemaTypes.RequestTestDriveInput = {
        contactMessage: data.userMsg,
        userFirstName: data.firstName,
        userLastName: data.lastName,
        userEmail: data.email,
        userPhone: data.phone,
        ...(financeCalculationResult &&
          financeCalculationResult.monthlyInstallment && {
            financeCalculationResult,
          }),
        paymentType,
        cashOption:
          paymentType === PaymentType.CashRange ? CashOption.Prepayment : null,
        requestTimeSlotOptions: [
          {
            requestTimeSlot: data.requestTimeSlot.requestTime,
            requestDate:
              data.requestTimeSlot.requestDate !== ''
                ? formatDateFormsRequest(
                    new Date(data.requestTimeSlot.requestDate)
                  )
                : data.requestTimeSlot.requestDate,
          },
          {
            requestTimeSlot: data.requestTimeSlotOptional.requestTime,
            requestDate:
              data.requestTimeSlotOptional.requestDate !== ''
                ? formatDateFormsRequest(
                    new Date(data.requestTimeSlotOptional.requestDate)
                  )
                : data.requestTimeSlotOptional.requestDate,
          },
        ],
      };

      try {
        await requestTestDrive(
          vehicleSku,
          dealerId,
          formData,
          token,
          carConfiguratorId
        );

        // dataLayer push
        pushAnalyticsEvent({
          event: 'test_drive',
          form_process: 'testdrive_formFinish',
          product_name: `${carManufacturer} ${carModel}`,
          product_id: vehicleSku,
        });

        pushAnalyticsEvent({
          event: 'dealerRequest',
          inquiry_topic: 'test drive', //e.g. dealer
          inquiry_details: '', //e.g.success
          dealer_name: dealerName.toLocaleLowerCase(), //e.g. berthold kerres
        });

        subscribeToNewsletter({
          email: formData.userEmail,
          source: 'SubscribeTestDrive',
        });

        dispatch(closeOverlay());

        setTimeout(
          () =>
            dispatch(
              openOverlay({
                children: (
                  // TODO: add headline, description and favorite button when ready
                  // bodyHeadline="My Headline"
                  // bodyDescription="My description"
                  // textLink={<TextLink onClick={onFavListClick} />}
                  <OverlaySuccess
                    bannerHeadline={
                      memoizedOverlaySuccessTranslations.SUBMISSION_THANK_YOU
                    }
                    bannerDescription={
                      memoizedOverlaySuccessTranslations.DEALER_CONFIRMATION_MSG
                    }
                    detailsHeadline={
                      memoizedOverlaySuccessTranslations.NEXT_STEPS_TEXT
                    }
                    details={successOverlayDetailsSteps}
                    ctaLabel={memoizedOverlaySuccessTranslations.BUTTON_TEXT}
                    onBtnClick={onBackToSearchClick}
                  />
                ),
                title: '',
                withPaddings: false,
              })
            ),
          100
        );
      } catch (e) {
        // the error is handled by the apollo errorHandler
        dispatch(setOverlayLoadingState(false));
      }
    },
    validationSchema: memoizedTestDriveBookingFormSchema,
  });

  return (
    <form className={className.form}>
      <div className={className.container}>
        <InputField
          placeholder={`${memoizedTestDriveBookingTranslations.FIRST_NAME}*`}
          errorMessage={formik.touched.firstName ? formik.errors.firstName : ''}
          name="firstName"
          customStyle="mt-30px"
          value={formik.values.firstName}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          darkBg={darkBg}
        />
        <InputField
          placeholder={`${memoizedTestDriveBookingTranslations.LAST_NAME}*`}
          errorMessage={formik.touched.lastName ? formik.errors.lastName : ''}
          customStyle="mt-30px"
          name="lastName"
          value={formik.values.lastName}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          darkBg={darkBg}
        />
        <InputField
          placeholder={`${memoizedTestDriveBookingTranslations.EMAIL_LABEL}*`}
          type="email"
          errorMessage={formik.touched.email ? formik.errors.email : ''}
          customStyle="mt-30px mb-[35px] text-purple80 monochrome:text-black"
          name="email"
          value={formik.values.email}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          darkBg={darkBg}
        />
        <InputField
          type="tel"
          errorMessage={formik.touched.phone ? formik.errors.phone : ''}
          placeholder={memoizedTestDriveBookingTranslations.PHONE_NUMBER}
          customStyle="mb-30px"
          name="phone"
          value={formik.values.phone}
          onChange={(event: React.FormEvent) =>
            formik.setFieldValue('phone', phoneInputChange(event))
          }
          onPaste={(event: React.ClipboardEvent) =>
            formik.setFieldValue('phone', phoneOnPasteSanitizer(event))
          }
          onKeyPress={allowedPhonePattern}
          onBlur={formik.handleBlur}
          darkBg={darkBg}
        />
        {(!openedAddMessage && (
          <div className="cursor-pointer">
            <TextLink
              icon={<Add32 className={className.addLink} />}
              text={memoizedTestDriveBookingTranslations.USER_MSG_LINK}
              onClick={handleAddMessageClick}
            />
          </div>
        )) || (
          <>
            <Textarea
              label={memoizedTestDriveBookingTranslations.USER_MSG_LABEL}
              name="userMsg"
              value={formik.values.userMsg}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              darkBg={darkBg}
            />
            {formik.errors.userMsg ? (
              <div className={className.error}>{formik.errors.userMsg}</div>
            ) : (
              ''
            )}
          </>
        )}
        <div className={className.appointmentSubtitle}>
          {memoizedTestDriveBookingTranslations.APPOINTMENT_SUBTITLE}
        </div>
        <div className={`mt-20px mb-30px ${className.dateTimeContainer}`}>
          <div className={className.datePicker}>
            <DatePicker
              name="requestTimeSlot.requestDate"
              placeholderText={`${memoizedTestDriveBookingTranslations.DATE_PLACEHOLDER_TEXT_1}*`}
              value={
                formik.values.requestTimeSlot.requestDate
                  ? formik.values.requestTimeSlot.requestDate
                  : undefined
              }
              locale={(intl.locale || intl.defaultLocale) as SupportedLocales}
              onChange={(value) =>
                formik.setFieldValue(`requestTimeSlot.requestDate`, value || '')
              }
              onBlur={formik.handleBlur}
              darkBg={darkBg}
              errorState={
                formik.touched.requestTimeSlot &&
                formik.touched.requestTimeSlot.requestDate &&
                !!formik.errors.requestTimeSlot?.requestDate
              }
              errorMessage={
                formik.touched.requestTimeSlot &&
                formik.touched.requestTimeSlot?.requestDate &&
                formik.errors.requestTimeSlot?.requestDate
                  ? formik.errors.requestTimeSlot.requestDate
                  : ''
              }
              minDate={minimumDate}
            />
          </div>
          <div className={className.searchDropdown}>
            <SearchDropdown
              placeholder={memoizedTestDriveBookingTranslations.TIMEWINDOW_1}
              value={
                !formik.values.requestTimeSlot.requestTime
                  ? null
                  : {
                      value: formik.values.requestTimeSlot.requestTime || '',
                      label:
                        translate(
                          intl,
                          `${'ui.FORMS.TEST_DRIVE_BOOKING.TIMEWINDOW_LABELS.'}${
                            formik.values.requestTimeSlot.requestTime
                          }`
                        ) || '',
                    }
              }
              options={timeSlotOptions}
              isSearchable={false}
              hasLabelAnimation={false}
              onChange={(selected) =>
                formik.setFieldValue(
                  `requestTimeSlot.requestTime`,
                  !selected ? null : (selected as Option).value
                )
              }
              darkBg={darkBg}
              fieldRequired={
                formik.touched.requestTimeSlot &&
                formik.touched.requestTimeSlot.requestTime &&
                !!formik.errors.requestTimeSlot?.requestTime
              }
            />
          </div>
        </div>
        {(!openedAdditionalFields && (
          <div className="cursor-pointer mb-20px">
            <TextLink
              icon={<Add32 className={className.addLink} />}
              text={
                memoizedTestDriveBookingTranslations.ALTERNATIVE_DATE_FIELDS
              }
              onClick={handleAdditionalFieldsClick}
            />
          </div>
        )) || (
          <div className={`mt-30px ${className.dateTimeContainer}`}>
            <div className={className.datePicker}>
              <DatePicker
                name="requestTimeSlotOptional.requestDate"
                placeholderText={
                  memoizedTestDriveBookingTranslations.DATE_PLACEHOLDER_TEXT_2
                }
                value={
                  formik.values.requestTimeSlotOptional.requestDate
                    ? formik.values.requestTimeSlotOptional.requestDate
                    : undefined
                }
                locale={(intl.locale || intl.defaultLocale) as SupportedLocales}
                onChange={(value) =>
                  formik.setFieldValue(
                    `requestTimeSlotOptional.requestDate`,
                    value || ''
                  )
                }
                onBlur={formik.handleBlur}
                darkBg={darkBg}
                minDate={minimumDate}
              />
            </div>
            <div className={className.searchDropdown}>
              <SearchDropdown
                placeholder={memoizedTestDriveBookingTranslations.TIMEWINDOW_2}
                value={
                  !formik.values.requestTimeSlotOptional.requestTime
                    ? null
                    : {
                        value:
                          formik.values.requestTimeSlotOptional.requestTime ||
                          '',
                        label:
                          translate(
                            intl,
                            `${'ui.FORMS.TEST_DRIVE_BOOKING.TIMEWINDOW_LABELS.'}${
                              formik.values.requestTimeSlotOptional.requestTime
                            }`
                          ) || '',
                      }
                }
                options={timeSlotOptions}
                isSearchable={false}
                hasLabelAnimation={false}
                onChange={(selected) =>
                  formik.setFieldValue(
                    `requestTimeSlotOptional.requestTime`,
                    !selected ? null : (selected as Option).value
                  )
                }
                darkBg={darkBg}
              />
            </div>
          </div>
        )}
        {!whitelabel && (
          <div className={className.newsletterPrivacy}>
            <NewsletterSubscribeCheckbox {...checkboxProps} />
            <PrivacyNotice
              translationKey="ui.FORMS.COMMON.PRIVACY_NOTICE.TEST_DRIVE_BOOKING"
              className={className.privacyNotice}
            />
          </div>
        )}
        <div className={className.requiredFieldNote}>
          {memoizedTestDriveBookingTranslations.REQUIRED_FIELD_NOTE}
        </div>
      </div>

      {process.env.NEXT_PUBLIC_FEATUREFLAG_RECAPTCHA && (
        <ReCaptchaValidator setStepValidator={setTokenValidator} />
      )}

      <Footer
        buttonText={memoizedTestDriveBookingTranslations.BUTTON_TEXT}
        onButtonClick={formik.handleSubmit}
        isButtonDisabled={formik.isSubmitting}
        className="!z-50"
        showButtonLoader={overlayLoadingState || false}
      />
    </form>
  );
};

export default TestDriveBookingForm;
